// Installed charging profiles, kept in OCPP 2.x shape (a 1.6 profile is a 2.x profile with one
// schedule), plus a composite schedule good enough for a simulator: the highest-precedence
// applicable profile's periods, each capped by the station maximum profile.

export interface SchedulePeriod {
  startPeriod: number;
  limit: number;
  numberPhases?: number;
}

export interface ChargingSchedule {
  id: number;
  chargingRateUnit: "A" | "W";
  chargingSchedulePeriod: SchedulePeriod[];
  duration?: number;
  startSchedule?: string;
  minChargingRate?: number;
}

export interface ChargingProfile {
  id: number;
  stackLevel: number;
  /** 2.x naming: ChargingStationMaxProfile, TxDefaultProfile, TxProfile, ... */
  chargingProfilePurpose: string;
  chargingProfileKind: string;
  recurrencyKind?: string;
  validFrom?: string;
  validTo?: string;
  transactionId?: string;
  chargingSchedule: ChargingSchedule[];
}

export interface InstalledProfile {
  evseId: number;
  profile: ChargingProfile;
}

export const fromProfile16 = (profile: any): ChargingProfile => ({
  id: profile.chargingProfileId,
  stackLevel: profile.stackLevel,
  chargingProfilePurpose:
    profile.chargingProfilePurpose === "ChargePointMaxProfile" ? "ChargingStationMaxProfile" : profile.chargingProfilePurpose,
  chargingProfileKind: profile.chargingProfileKind,
  ...(profile.recurrencyKind && { recurrencyKind: profile.recurrencyKind }),
  ...(profile.validFrom && { validFrom: profile.validFrom }),
  ...(profile.validTo && { validTo: profile.validTo }),
  ...(profile.transactionId !== undefined && { transactionId: String(profile.transactionId) }),
  chargingSchedule: [
    {
      id: profile.chargingProfileId,
      chargingRateUnit: profile.chargingSchedule.chargingRateUnit,
      chargingSchedulePeriod: profile.chargingSchedule.chargingSchedulePeriod,
      ...(profile.chargingSchedule.duration !== undefined && { duration: profile.chargingSchedule.duration }),
      ...(profile.chargingSchedule.startSchedule && { startSchedule: profile.chargingSchedule.startSchedule }),
      ...(profile.chargingSchedule.minChargingRate !== undefined && { minChargingRate: profile.chargingSchedule.minChargingRate }),
    },
  ],
});

export const toProfile16 = (profile: ChargingProfile): any => {
  const schedule = profile.chargingSchedule[0];
  return {
    chargingProfileId: profile.id,
    stackLevel: profile.stackLevel,
    chargingProfilePurpose:
      profile.chargingProfilePurpose === "ChargingStationMaxProfile" ? "ChargePointMaxProfile" : profile.chargingProfilePurpose,
    chargingProfileKind: profile.chargingProfileKind,
    ...(profile.recurrencyKind && { recurrencyKind: profile.recurrencyKind }),
    ...(profile.validFrom && { validFrom: profile.validFrom }),
    ...(profile.validTo && { validTo: profile.validTo }),
    chargingSchedule: {
      chargingRateUnit: schedule.chargingRateUnit,
      chargingSchedulePeriod: schedule.chargingSchedulePeriod,
      ...(schedule.duration !== undefined && { duration: schedule.duration }),
      ...(schedule.startSchedule && { startSchedule: schedule.startSchedule }),
      ...(schedule.minChargingRate !== undefined && { minChargingRate: schedule.minChargingRate }),
    },
  };
};

const PRECEDENCE = ["TxProfile", "TxDefaultProfile"];

export class ChargingProfileStore {
  private profiles: InstalledProfile[] = [];

  all(): InstalledProfile[] {
    return this.profiles.map(p => ({ evseId: p.evseId, profile: { ...p.profile } }));
  }

  /** Install, replacing a profile with the same id, or with the same purpose and stack level. */
  install(evseId: number, profile: ChargingProfile) {
    this.profiles = this.profiles.filter(
      p =>
        p.profile.id !== profile.id &&
        !(p.evseId === evseId && p.profile.chargingProfilePurpose === profile.chargingProfilePurpose && p.profile.stackLevel === profile.stackLevel)
    );
    this.profiles.push({ evseId, profile });
  }

  /** Remove matching profiles; returns how many were removed. No criteria clears everything. */
  clear(criteria: { id?: number; evseId?: number; purpose?: string; stackLevel?: number } = {}): number {
    const before = this.profiles.length;
    this.profiles = this.profiles.filter(
      p =>
        !(
          (criteria.id === undefined || p.profile.id === criteria.id) &&
          (criteria.evseId === undefined || p.evseId === criteria.evseId) &&
          (criteria.purpose === undefined || p.profile.chargingProfilePurpose === criteria.purpose) &&
          (criteria.stackLevel === undefined || p.profile.stackLevel === criteria.stackLevel)
        )
    );
    return before - this.profiles.length;
  }

  /** Drop TxProfiles bound to an ended transaction (they only live as long as it). */
  endTransaction(transactionId: string) {
    this.profiles = this.profiles.filter(
      p => !(p.profile.chargingProfilePurpose === "TxProfile" && p.profile.transactionId === transactionId)
    );
  }

  find(filter: (installed: InstalledProfile) => boolean) {
    return this.profiles.filter(filter);
  }

  /**
   * Composite schedule for `evseId` over `duration` seconds, or null when no profile applies.
   * `transactionId` selects which TxProfile applies.
   */
  composite(evseId: number, duration: number, unit?: "A" | "W", transactionId?: string): ChargingSchedule | null {
    const applicable = (purpose: string) =>
      this.profiles
        .filter(
          p =>
            p.profile.chargingProfilePurpose === purpose &&
            (p.evseId === evseId || p.evseId === 0) &&
            (purpose !== "TxProfile" || p.profile.transactionId === undefined || p.profile.transactionId === transactionId)
        )
        .sort((a, b) => b.profile.stackLevel - a.profile.stackLevel);

    const base = PRECEDENCE.map(purpose => applicable(purpose)[0]).find(Boolean);
    const max = applicable("ChargingStationMaxProfile")[0];
    const source = base ?? max;
    if (!source) return null;

    const schedule = source.profile.chargingSchedule[0];
    if (unit && schedule.chargingRateUnit !== unit) return null;
    const cap = max && max.profile.chargingSchedule[0].chargingRateUnit === schedule.chargingRateUnit
      ? max.profile.chargingSchedule[0].chargingSchedulePeriod[0]?.limit
      : undefined;
    return {
      id: schedule.id,
      chargingRateUnit: schedule.chargingRateUnit,
      duration: Math.min(duration, schedule.duration ?? duration),
      chargingSchedulePeriod: schedule.chargingSchedulePeriod
        .filter(period => period.startPeriod < duration)
        .map(period => ({ ...period, limit: cap === undefined ? period.limit : Math.min(period.limit, cap) })),
    };
  }
}
